import moment from "moment"
import { UserSegment } from "../../definitions"
import { EXPORT_DATE_FORMAT, itemWithId } from "."
import { PaymentRequest } from "../../definitions/purchases"
import { GenericProduct, LocalProduct, SpecificProduct } from "../../definitions/Product"
import { Purchase } from "../../definitions/Purchase"

const formattedAmount = (amount: number, decimalPlaces = 2) => {

  if(decimalPlaces > 0) {
    return `${amount.toFixed(decimalPlaces)}`.replace('.', ',')
  } else {
    return `${amount}`.replace('.', ',')
  }
}

const formattedMetadataValue = (value: unknown): string => {
  if(typeof value !== 'string') return ''
  return /[;"\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

export const paymentMethodLabels: Record<string, string> = {
  'american_express': 'American Express',
  'amex': 'American Express',
  'apple_pay': 'Apple Pay',
  'bancontact': 'Bancontact',
  'bank-transfer': 'Virement bancaire',
  'default': 'Carte bancaire',
  'ideal': 'iDEAL',
  'mybank': 'MyBank',
  'satispay': 'Satispay',
}

// Use the same resort-to-network relationship as the existing network filter.
const getOrionProperties = (purchase: Purchase, resorts: any[], resellers: any[]) => {
  const resort = resorts.find(r => r.id === purchase.resort)
  const reseller = purchase.resort ? resellers.find(r => r.resorts?.includes(purchase.resort)) : undefined
  return {
    memberOfOrionTnNetwork: Boolean(resort?.member_of_orion_tn_network),
    orionTnOfficialDistributor: Boolean(reseller?.orion_tn_official_distributor),
    orionTnOfficialLeadGenerator: Boolean(reseller?.orion_tn_official_lead_generator),
  }
}

export const buildPurchasesExport = ({
  purchases,
  paymentRequests,
  rangeStart,
  rangeEnd,
  legacyProducts,
  genericProducts,
  specificProducts,
  localProducts,
  resorts,
  domains,
  durations,
  regions,
  resellers,
  partners,
  userSegment
}: {
  purchases: Purchase[]
  paymentRequests: PaymentRequest[] // Not used in the export, but included for completeness
  rangeStart?: string // YYYY-MM-DD
  rangeEnd?: string // YYYY-MM-DD
  legacyProducts?: any[]
  genericProducts?: any[]
  specificProducts?: any[]
  localProducts?: any[]
  resorts?: any[]
  domains?: any[]
  durations?: any[]
  regions?: any[],
  resellers?: any[]
  partners?: any[]
  userSegment: UserSegment | null,
}): string => {

  // update 18/12/2024
  const columns = [
    'Purchase ID', // A
    'Generic product name (WebApp)', // B
    'Specific product name (WebApp)', // C
    'Local product ID (WebApp)', // D
    'Product ID (WTW / Mutuaide)', // E
    'PPV unitaire', // F
    'Q-P tech unitaire', // G
    'Com distrib unitaire', // H
    'Operator share (unitaire)', // I
    'Clubs share (unitaire)', // J
    'Others share (unitaire)', // K
    'Publisher share (unitaire)', // L
    'Purchase date', // M
    'Nombre de produits achetés', // N
    'Amount paid', // O
    'Com distrib à percevoir', // P
    'Refunded', // Q
    'Refunded at', // R
    'Refunded amount', // S
    'Voucher', // T
    'Customer email', // U
    'Customer name', // V
    'Customer company', // W
    'Insured persons', // X
    'Resort', // Y
    'Domain', // Z
    'Duration', // AA
    'Dates of insurance', // AB
    'Contract url', // AC
    'Language', // AD
    'Payment method type', // AE
    'Payment method country', // AF
    'API purchase', // AG
    'API partner', // AH
    'Uses pricing snapshot', // AI
    'Member of Orion TN network', // AJ
    'Orion TN official distributor', // AK
    'Orion TN official lead generator', // AL
    'Nombre de jours assurés (total)', // AM
    'Metadata (activity)', // AN
    'Metadata (region)', // AO
  ]

  const columnSeparator = ';'

  const csvHeader = columns.join(columnSeparator) + '\n'

  let csv = csvHeader
  let exportCount = 0
  let totalPaid = 0
  let totalDistributorAmount = 0

  let filteredPurchases = purchases
  if(userSegment) {
    filteredPurchases = purchases.filter(purchase => {
      const orionProperties = getOrionProperties(purchase, resorts || [], resellers || [])
      for(const key of Object.keys(orionProperties) as (keyof typeof orionProperties)[]) {
        if(userSegment[key] != null && userSegment[key] !== orionProperties[key]) {
          return false
        }
      }
      if(userSegment.product) {
        if(purchase.product_generic !== userSegment.product && purchase.product_specific !== userSegment.product) {
          return false
        }
      }
      if(userSegment.domain && purchase.domain !== userSegment.domain) {
        return false
      }
      // if(userSegment.resort && purchase.resort !== userSegment.resort) {
      //   return false
      // }
      if(userSegment.resorts && userSegment.resorts.length > 0 && (!purchase.resort || !userSegment.resorts.includes(purchase.resort))) {
        return false
      }
      if(userSegment.numberOfBeneficiaries) {
        const numberOfBeneficiaries = purchase.people.length
        const { relation, value } = userSegment.numberOfBeneficiaries
        if(relation === 'moreThan' && numberOfBeneficiaries <= value) {
          return false
        }
        if(relation === 'lessThan' && numberOfBeneficiaries >= value) {
          return false
        }
        if(relation === 'equalTo' && numberOfBeneficiaries !== value) {
          return false
        }
      }
      if(userSegment.durationType && userSegment.duration) {
        const durationDays = (purchase.dates_formatted || purchase.dates || [])?.length
        const { relation, value } = userSegment.duration
        if(userSegment.durationType === 'days') {
          if(relation === 'moreThan' && durationDays <= value) {
            return false
          }
          if(relation === 'lessThan' && durationDays >= value) {
            return false
          }
          if(relation === 'equalTo' && durationDays !== value) {
            return false
          }
        } else if(userSegment.durationType === 'season' && durationDays > 0) {
          // If durationType is 'season', we consider any purchase with dates as a 'days' purchase
          return false
        }
      }
      if(userSegment.language && purchase.language?.toLowerCase() !== userSegment.language.toLowerCase()) {
        return false
      }
      const paymentRequest: PaymentRequest = itemWithId(paymentRequests, purchase.id)
      if(userSegment.country && paymentRequest?.card?.country?.toLowerCase() !== userSegment.country.toLowerCase()) {
        return false
      }
      if(userSegment.paymentMethod && paymentRequest?.payment_method?.toLowerCase() !== userSegment.paymentMethod.toLowerCase()) {
        return false
      }
      if(userSegment.regions && userSegment.regions.length > 0) {
        const purchaseResort = resorts?.find(r => r.id === purchase.resort)
        const purchaseRegionId = purchaseResort?.region
        if(!purchaseRegionId || !userSegment.regions.includes(purchaseRegionId)) {
          return false
        }
      }
      if(userSegment.resellers && userSegment.resellers.length > 0) {
        const purchaseReseller = resellers?.find((r: any) => r.resorts?.includes(purchase.resort))
        if(!purchaseReseller || !userSegment.resellers.includes(purchaseReseller.id)) {
          return false
        }
      }
      if(userSegment.apiPurchase !== null && userSegment.apiPurchase !== undefined) {
        if(Boolean(purchase.api_purchase) !== userSegment.apiPurchase) {
          return false
        }
      }
      if(userSegment.apiPartner) {
        if(purchase.api_partner !== userSegment.apiPartner) {
          return false
        }
      }
      return true
    })
  }

  const sortedPurchases = [...filteredPurchases]
  sortedPurchases.sort((a, b) => {
    const aDate = a.paid_at || a.meta?.updated || a.meta?.created || 0
    const bDate = b.paid_at || b.meta?.updated || b.meta?.created || 0
    if(aDate < bDate) return 1
    if(aDate > bDate) return -1
    return 0
  })
  for(let purchase of sortedPurchases) {
    const paymentRequest: PaymentRequest = itemWithId(paymentRequests, purchase.id)
    if(!paymentRequest) {
      console.warn(`No payment request found for purchase ${purchase.id}`)
    }
    const purchaseMoment = moment(purchase.paid_at || purchase.meta?.updated || purchase.meta?.created)
    const purchaseYear = purchaseMoment.year()
    const purchaseMonth = purchaseMoment.month() + 1
    const purchaseDay = purchaseMoment.date()
    if(rangeStart) {
      const [exportRangeStartYear, exportRangeStartMonth, exportRangeStartDay] = rangeStart.split('-').map(e => parseInt(e))
      if(purchaseYear < exportRangeStartYear) {
        continue
      }
      if(purchaseYear === exportRangeStartYear && purchaseMonth < exportRangeStartMonth) {
        continue
      }
      if(purchaseYear === exportRangeStartYear && purchaseMonth === exportRangeStartMonth && purchaseDay < exportRangeStartDay) {
        continue
      }
    }
    if(rangeEnd) {
      const [exportRangeEndYear, exportRangeEndMonth, exportRangeEndDay] = rangeEnd.split('-').map(e => parseInt(e))      
      if(purchaseYear > exportRangeEndYear) {
        continue
      }
      if(purchaseYear === exportRangeEndYear && purchaseMonth > exportRangeEndMonth) {
        continue
      }
      if(purchaseYear === exportRangeEndYear && purchaseMonth === exportRangeEndMonth && purchaseDay > exportRangeEndDay) {
        continue
      }
    }

    const localProduct = itemWithId(localProducts || [], purchase.product_local) as LocalProduct
    const specificProduct = itemWithId(specificProducts || [], purchase.product_specific) as SpecificProduct
    const genericProduct = itemWithId(genericProducts || [], purchase.product_generic) as GenericProduct
    const legacyProduct = itemWithId(legacyProducts || [], purchase.product)

    const productData = {...(genericProduct || {}), ...(specificProduct || {}), ...(localProduct || {}), ...(legacyProduct || {})}

    const usePricingSnapshot = purchase.pricing_snapshot && Object.keys(purchase.pricing_snapshot).length > 0

    const resort = itemWithId(resorts || [], purchase.resort)
    const domain = itemWithId(domains || [], purchase.domain)
    const duration = itemWithId(durations || [], purchase.duration)
    const purchaseId = purchase.id
    // const productName = productData?.name?.fr || purchase.product_name || 'N/A'
    const genericProductName = (genericProduct || legacyProduct)?.name?.fr || purchase.product_name || 'N/A'
    const specificProductName = specificProduct?.name?.fr || purchase.product_name || 'N/A'
    // const legacyProductId = legacyProduct?.id || purchase.product || 'N/A'
    const localProductId = localProduct?.id || purchase.product_local || 'N/A'
    // const specificProductId = specificProduct?.id || purchase.product_specific || 'N/A'
    // const genericProductId = genericProduct?.id || purchase.product_generic || 'N/A'
    // const productNameWtw = productData?.name?.fr || purchase.product_name || 'N/A'
    const productIdWtw = productData?.internal_id || 'N/A'
    const purchaseDate = purchase.paid_at ? moment(purchase.paid_at).format(EXPORT_DATE_FORMAT) : purchase.meta?.updated ? moment(purchase.meta.updated).format('DD/MM/YYYY HH:mm') : 'N/A'
    const amountPaid: number = purchase.amount / 100 // purchase.amount is in cents, we convert to euros
    const datesArray = (purchase.dates_formatted?.length ? purchase.dates_formatted : undefined) || purchase.dates?.map(d => moment(d).format('DD/MM/YYYY')) || []
    // const productOption = product.options[`${purchase.resort}/${purchase.domain}/${purchase.duration}`]
    // const distributorUnitPrice = 'N/A' // productUnitPrice * 0.8
    // const distributorAmount = 'N/A' // distributorUnitPrice * purchase.quantity
    const numberOfProducts = purchase.people.length *  (duration.dates_required ? datesArray.length : 1)
    const productUnitPrice: number = (purchase.amount / numberOfProducts) / 100 // we divide the total amount by the number of products to get the unit price, and convert to euros
    const totalTechnicalShare: number = usePricingSnapshot ?
      ((purchase.pricing_snapshot?.share_insurer || 0) + (purchase.pricing_snapshot?.share_broker || 0) + (purchase.pricing_snapshot?.share_co_broker || 0)) / 100
      : (specificProduct?.share_insurer || 0) + (specificProduct?.share_broker || 0) + (specificProduct?.share_co_broker || 0)
    const priceHt: number = usePricingSnapshot ? (purchase.pricing_snapshot?.price_ht_after_discount || 0) / 100 : localProduct?.price_ht || 0
    const customerEmail = purchase.buyer.email
    const customerName = `${purchase.buyer.firstname} ${purchase.buyer.lastname}`
    const customerCompany = purchase.buyer.company_name || ''
    const insuredPersons = purchase.people.map(p => `${p.firstname} ${p.lastname}`).join(', ')
    const resortName = resort?.name?.fr || 'N/A'
    const domainName = domain.title.fr
    const durationName = duration.title.fr
    const dates = datesArray.join(', ')
    // const proofsOfPurchase = (purchase.proofs_of_purchase || []).join(', ')
    const contractUrl = purchase.contract_url || 'N/A'
    const refunded = purchase.status === "refunded" ? "YES" : "NO"
    const refundedAt = purchase.status === "refunded" ? purchase.meta.updated ? moment(purchase.meta.updated).format(EXPORT_DATE_FORMAT) : 'N/A' : ''
    const refundedAmount: number = purchase.status === "refunded" ? purchase.amount_refunded ? purchase.amount_refunded / 100 : 0 : 0
    const operatorShare: number = usePricingSnapshot ? (purchase.pricing_snapshot?.share_operator || 0) / 100 : localProduct?.share_operator || 0
    const clubShare: number = usePricingSnapshot ? (purchase.pricing_snapshot?.share_club || 0) / 100 : localProduct?.share_club || 0
    const otherShare: number = usePricingSnapshot ? (purchase.pricing_snapshot?.share_other || 0) / 100 : localProduct?.share_other || 0
    const publisherShare: number = usePricingSnapshot ? (purchase.pricing_snapshot?.share_webapp || 0) / 100 : localProduct?.share_webapp || 0
    // reduce publisher share by the same percentage as the discount applied to the price_ht, if there is a discount
    const publisherShareAfterDiscount: number = publisherShare * (priceHt / (purchase.pricing_snapshot?.price_ht ? purchase.pricing_snapshot.price_ht / 100 : localProduct?.price_ht || priceHt))
    const distributorShare: number = priceHt - totalTechnicalShare
    const distributorAmount: number = distributorShare * numberOfProducts
    const voucherCode = purchase.voucher_code || ''
    const language = (purchase.language || '').toUpperCase()
    const paymentMethodType = paymentRequest?.payment_method ? paymentMethodLabels[paymentRequest.payment_method] || paymentRequest.payment_method : 'N/A'
    const paymentMethodCountry = paymentRequest?.card?.country || 'N/A'
    const apiPurchase = purchase.api_purchase ? 'YES' : 'NO'
    const apiPartner = itemWithId(partners || [], purchase.api_partner)
    const apiPartnerLabel = apiPartner?.name?.fr || apiPartner?.name || apiPartner?.company_name || apiPartner?.title || purchase.api_partner || 'N/A'

    const orionProperties = getOrionProperties(purchase, resorts || [], resellers || [])
    const insuredDays = datesArray.length > 0 ? String(datesArray.length * purchase.people.length) : 'non applicable'

    const line: string[] = [
      purchaseId, // A
      genericProductName, // B
      specificProductName, // C
      localProductId, // D
      productIdWtw, // E
      formattedAmount(productUnitPrice, 0), // F
      formattedAmount(totalTechnicalShare, 0), // G
      formattedAmount(distributorShare, 0), // H
      formattedAmount(operatorShare, 0), // I
      formattedAmount(clubShare, 0), // J
      formattedAmount(otherShare, 0), // K
      formattedAmount(publisherShareAfterDiscount, 0), // L
      purchaseDate, // M
      numberOfProducts, // N
      formattedAmount(amountPaid), // O
      formattedAmount(distributorAmount, 0), // P
      refunded, // Q
      refundedAt, // R
      formattedAmount(refundedAmount, 0), // S
      voucherCode, // T
      customerEmail, // U
      customerName, // V
      customerCompany, // W
      insuredPersons, // X
      resortName, // Y
      domainName, // Z
      durationName, // AA
      dates, // AB
      contractUrl, // AC
      language, // AD
      paymentMethodType, // AE
      paymentMethodCountry, // AF
      apiPurchase, // AG
      apiPartnerLabel, // AH
      usePricingSnapshot ? 'YES' : 'NO', // AI
      orionProperties.memberOfOrionTnNetwork ? 'YES' : 'NO', // AJ
      orionProperties.orionTnOfficialDistributor ? 'YES' : 'NO', // AK
      orionProperties.orionTnOfficialLeadGenerator ? 'YES' : 'NO', // AL
      insuredDays, // AM
      formattedMetadataValue(purchase.purchase_metadata?.activity), // AN
      formattedMetadataValue(purchase.purchase_metadata?.region), // AO

    ]
    csv += line.join(columnSeparator) + '\n'
    exportCount++
    totalPaid += purchase.amount
    totalDistributorAmount += distributorAmount
  }
  if(exportCount > 0) {
    const totalPaidColumnIndex = columns.indexOf('Amount paid')
    const comDistribAPercevoirColumnIndex = columns.indexOf('Com distrib à percevoir')
    const line = Array(columns.length).fill('')
    line[totalPaidColumnIndex] = formattedAmount(totalPaid / 100)
    line[comDistribAPercevoirColumnIndex] = formattedAmount(totalDistributorAmount)
    csv += line.join(columnSeparator) + '\n'
  }

  return csv
}
